import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { FileOutput, Printer, Search, Car, FileText, HelpCircle, Download, ExternalLink, Loader2, BookOpenText } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ButirSheet, BUTIR_CSS, paginateMonths, MONTHS_BM } from "../components/ButirSheets";
import JanaPenyelenggaraan from "./JanaPenyelenggaraan";

const TABS = [
  { key: "penggunaan", label: "Penggunaan" },
  { key: "penyelenggaraan", label: "Penyelenggaraan" },
  { key: "tol-parkir", label: "Tol dan Parkir", disabled: true },
  { key: "saman", label: "Saman", disabled: true },
];

const YEARS = [2024, 2025, 2026];
const SHEET_W = 1123;
const SHEET_H = 795;

const PENJELASAN = [
  { k: "Tarikh & Masa (Mulai/Hingga)", v: "Diambil daripada masa mula & tamat log perjalanan (trip) yang direkod pemandu, serta masa transaksi pada rekod Isi Minyak." },
  { k: "Nama Pemandu", v: "Nama pemandu yang ditugaskan pada tempahan/log perjalanan tersebut." },
  { k: "Tujuan dan Destinasi (dari - ke)", v: "Digabung automatik: Lokasi bertolak → Destinasi → Kembali, diikuti tujuan rasmi yang diisi semasa tempahan." },
  { k: "Nama Pelulus", v: "Pegawai yang memberi kelulusan akhir tempahan (Pentadbir). Ruang tandatangan dibiarkan untuk ditandatangan fizikal." },
  { k: "Nama Pengguna", v: "Pemohon tempahan (staf yang menggunakan kenderaan)." },
  { k: "Bacaan Odometer (Km)", v: "Bacaan odometer mula/ tamat yang direkod pada log perjalanan (atau dari telematik jika disambung)." },
  { k: "Jarak Perjalanan (Km)", v: "Dikira automatik: Odometer Tamat − Odometer Mula bagi setiap trip." },
  { k: "No. Resit Pembelian & RM", v: "Daripada modul Isi Minyak — rujukan resit dijana (cth: R-1F5BDE) berserta jumlah RM. Kos bahan api pada log perjalanan turut dipaparkan." },
  { k: "Liter/ Kilowatt", v: "Kuantiti liter (atau kWj bagi EV) daripada rekod Isi Minyak dan log perjalanan." },
  { k: "Arahan Khas Pengguna", v: "Catatan arahan khas yang diisi pada borang tempahan." },
];

const FORMULA = [
  { k: "(b) Jumlah Jarak", v: "Hasil tambah semua jarak perjalanan dalam bulan tersebut." },
  { k: "(c) Jumlah Liter/Kilowatt", v: "Hasil tambah semua liter/kWj (Isi Minyak + log perjalanan)." },
  { k: "(d) Jumlah Pembelian (RM)", v: "Hasil tambah semua nilai RM pembelian bahan api." },
  { k: "(e) Kadar = (b) ÷ (c)", v: "Kadar penggunaan bahan api Km/Liter@Kilowatt — dibandingkan dengan ambang polisi agensi anda." },
];

export default function JanaLaporan() {
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState("penggunaan");
  const [vehicles, setVehicles] = useState([]);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [month, setMonth] = useState("all");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dl, setDl] = useState(false);
  const previewRef = useRef(null);
  const [scale, setScale] = useState(0.6);

  useEffect(() => { api.get("/vehicles").then((r) => setVehicles(r.data)); }, []);

  useEffect(() => {
    const vid = searchParams.get("vehicle");
    if (vid && vehicles.length > 0 && !selected) {
      const v = vehicles.find((x) => x.id === vid);
      if (v) { setSelected(v); setQuery(v.registration_number); }
    }
  }, [vehicles, searchParams, selected]);

  useEffect(() => {
    const el = previewRef.current;
    if (!el) return;
    const update = () => setScale(Math.min(1, (el.clientWidth - 8) / SHEET_W));
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [selected]);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return vehicles.filter((v) => `${v.registration_number} ${v.make} ${v.model}`.toLowerCase().includes(q)).slice(0, 8);
  }, [query, vehicles]);

  useEffect(() => {
    if (!selected) { setData(null); return; }
    setLoading(true);
    const params = { year: Number(year) };
    if (month !== "all") params.month = Number(month);
    api.get(`/reports/buku-log/${selected.id}`, { params })
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, [selected, year, month]);

  const pages = useMemo(() => {
    if (!data) return [];
    return paginateMonths(data.months, `${year}-${String(month === "all" ? 1 : month).padStart(2, "0")}`);
  }, [data, year, month]);

  const monthQS = month !== "all" ? `&month=${month}` : "";

  const downloadDoc = async (openGoogleDocs) => {
    setDl(true);
    try {
      const params = { year: Number(year) };
      if (month !== "all") params.month = Number(month);
      const res = await api.get(`/reports/buku-log/${selected.id}/doc`, { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Butir_Penggunaan_${selected.registration_number.replace(/ /g, "")}_${year}${month !== "all" ? `-${String(month).padStart(2, "0")}` : ""}.doc`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      if (openGoogleDocs) {
        window.open("https://docs.google.com/document/u/0/", "_blank");
        toast.info("Fail .doc dimuat turun. Dalam Google Docs: Fail → Buka → Muat Naik, pilih fail tersebut.", { duration: 8000 });
      } else {
        toast.success("Fail MS Word (.doc) berjaya dimuat turun");
      }
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setDl(false);
    }
  };

  return (
    <div className="space-y-6 fade-up" data-testid="jana-laporan-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
          <FileOutput className="h-6 w-6 text-[#10056E]" /> Jana Laporan
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          {tab === "penyelenggaraan"
            ? <>Jana halaman rasmi "Rekod Penyelenggaraan Berjadual" mengikut Pekeliling Perbendaharaan Malaysia <b>WP 4.1</b>. Data diisi automatik daripada rekod Penyelenggaraan.</>
            : <>Jana halaman rasmi "Butir-Butir Penggunaan Kenderaan" mengikut Pekeliling Perbendaharaan Malaysia <b>WP 4.1 (berkuat kuasa 6 Julai 2026) — Lampiran F1</b>. Semua data diisi automatik daripada Buku Log digital.</>}
        </p>
      </div>

      <div className="flex gap-1 bg-white border border-slate-200 rounded-xl shadow-sm p-1.5 w-fit max-w-full overflow-x-auto" data-testid="jana-tabs">
        {TABS.map((t) => (
          <button key={t.key} disabled={t.disabled}
            onClick={() => !t.disabled && setTab(t.key)}
            data-testid={`jana-tab-${t.key}`}
            className={`px-4 h-9 rounded-lg text-sm font-semibold whitespace-nowrap transition-colors ${
              tab === t.key ? "bg-[#10056E] text-white shadow-sm"
                : t.disabled ? "text-slate-300 cursor-not-allowed"
                : "text-slate-600 hover:bg-slate-100"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === "penyelenggaraan" && <JanaPenyelenggaraan />}

      {tab === "penggunaan" && (
      <div className="grid lg:grid-cols-[340px_1fr] gap-5 items-start">
        <div className="space-y-5">
          <Card className="border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle className="font-heading text-base">1. Pilih Kenderaan & Tempoh</CardTitle>
              <CardDescription>Laporan dijana bagi satu kenderaan.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-1.5 relative">
                <Label>Kenderaan</Label>
                <div className="relative">
                  <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input data-testid="jana-vehicle-search" className="pl-9" placeholder="Cari nombor plat / model..."
                    value={query} onChange={(e) => { setQuery(e.target.value); setSelected(null); }} />
                </div>
                {results.length > 0 && !selected && (
                  <div className="absolute z-50 top-full mt-1 inset-x-0 bg-white border rounded-lg shadow-lg max-h-56 overflow-y-auto" data-testid="jana-vehicle-results">
                    {results.map((v) => (
                      <button key={v.id} onClick={() => { setSelected(v); setQuery(v.registration_number); }}
                        data-testid={`jana-vehicle-${v.registration_number}`}
                        className="w-full text-left px-3 py-2 text-xs hover:bg-slate-50 border-b last:border-0">
                        <b>{v.registration_number}</b> — {v.make} {v.model} · {v.category}
                      </button>
                    ))}
                  </div>
                )}
                {selected && (
                  <div className="flex items-center gap-2 text-xs bg-indigo-50/70 border border-indigo-100 rounded-lg p-2.5">
                    <Car className="h-4 w-4 text-[#10056E]" />
                    <span><b>{selected.registration_number}</b> — {selected.make} {selected.model} ({selected.fuel_type})</span>
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Tahun</Label>
                  <Select value={year} onValueChange={setYear}>
                    <SelectTrigger data-testid="jana-year-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">
                      {YEARS.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Bulan</Label>
                  <Select value={month} onValueChange={setMonth}>
                    <SelectTrigger data-testid="jana-month-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="all">Semua Bulan</SelectItem>
                      {MONTHS_BM.map((m, i) => <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle className="font-heading text-base">2. Muat Turun</CardTitle>
              <CardDescription>Simpan laporan dalam format pilihan anda.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2.5">
              <Button disabled={!selected} className="w-full bg-[#10056E] hover:bg-[#2a17a8]" data-testid="jana-pdf-btn"
                onClick={() => window.open(`/cetak/jana-laporan/${selected.id}?year=${year}${monthQS}`, "_blank")}>
                <Printer className="h-4 w-4 mr-2" /> PDF (Cetak / Simpan PDF)
              </Button>
              <Button disabled={!selected || dl} variant="outline" className="w-full border-[#10056E] text-[#10056E] hover:bg-indigo-50" data-testid="jana-word-btn"
                onClick={() => downloadDoc(false)}>
                {dl ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />} MS Word (.doc)
              </Button>
              <Button disabled={!selected || dl} variant="outline" className="w-full" data-testid="jana-gdocs-btn"
                onClick={() => downloadDoc(true)}>
                <ExternalLink className="h-4 w-4 mr-2" /> Google Docs (import .doc)
              </Button>
              <button disabled={!selected} data-testid="jana-fullbook-link"
                onClick={() => window.open(`/cetak/buku-log/${selected.id}?year=${year}`, "_blank")}
                className="w-full text-center text-[11px] text-muted-foreground hover:text-[#10056E] underline underline-offset-2 pt-1 disabled:opacity-40">
                <BookOpenText className="h-3 w-3 inline mr-1" />Cetak Buku Log penuh (semua halaman)
              </button>
            </CardContent>
          </Card>

          <Card className="border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle className="font-heading text-base flex items-center gap-2">
                <HelpCircle className="h-4 w-4 text-[#10056E]" /> Bagaimana Data Dijana
              </CardTitle>
              <CardDescription>Sumber & pengiraan automatik setiap kolum.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3" data-testid="jana-explanation-panel">
              <div className="space-y-2">
                {PENJELASAN.map((p) => (
                  <div key={p.k} className="text-xs border-l-2 border-[#10056E]/30 pl-2.5">
                    <b className="text-slate-800">{p.k}</b>
                    <p className="text-muted-foreground mt-0.5 leading-relaxed">{p.v}</p>
                  </div>
                ))}
              </div>
              <div className="bg-indigo-50/70 border border-indigo-100 rounded-lg p-3 space-y-1.5">
                <p className="text-xs font-bold text-[#10056E]">Kadar Penggunaan Bahan Api Bulanan</p>
                {FORMULA.map((f) => (
                  <p key={f.k} className="text-[11px] text-slate-700"><b>{f.k}</b> — {f.v}</p>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        <div ref={previewRef} className="min-w-0" data-testid="jana-preview-area">
          {!selected && (
            <div className="border-2 border-dashed border-slate-200 rounded-xl bg-white/60 py-24 text-center">
              <FileText className="h-10 w-10 text-slate-300 mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">Pilih kenderaan untuk melihat pratonton halaman A4 sebenar.</p>
            </div>
          )}
          {selected && loading && <p className="text-sm text-muted-foreground py-16 text-center">Menjana pratonton...</p>}
          {selected && data && !loading && (
            <div className="space-y-3">
              <div className="flex flex-wrap items-center gap-2 text-xs">
                <Badge variant="secondary" data-testid="jana-badge-siri">No. Siri: {data.no_siri}</Badge>
                <Badge variant="secondary">{data.total_trips} log perjalanan</Badge>
                <Badge variant="secondary">{data.total_refuels} rekod isi minyak</Badge>
                <Badge variant="secondary">{pages.length} halaman A4</Badge>
              </div>
              {data.months.length === 0 && (
                <p className="text-xs text-muted-foreground bg-amber-50 border border-amber-200 rounded-lg p-3">
                  Tiada rekod bagi tempoh ini — halaman kosong tetap dijana untuk diisi secara manual.
                </p>
              )}
              <style>{BUTIR_CSS}</style>
              <div className="butir-doc space-y-4">
                {pages.map((p, i) => (
                  <div key={i} style={{ height: SHEET_H * scale, overflow: "hidden" }} data-testid={`jana-sheet-${i}`}>
                    <div style={{ transform: `scale(${scale})`, transformOrigin: "top left", width: SHEET_W }}>
                      <ButirSheet data={data} page={p} footer={`M.S. ${i + 1}/${pages.length}`} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      )}
    </div>
  );
}
